import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MY_ORGANIZATIONS, GET_MY_PROJECTS } from '../graphql/organizations';

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  maxMembers: number;
  maxProjects: number;
  maxContexts: number;
}

interface Project {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  currentProject: Project | null;
  organizations: Organization[];
  projects: Project[];
  setCurrentOrganization: (org: Organization | null) => void;
  setCurrentProject: (project: Project | null) => void;
  loading: boolean;
  refetchOrganizations: () => void;
  refetchProjects: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  // Fetch organizations
  const { data: orgsData, loading: orgsLoading, refetch: refetchOrgs } = useQuery(GET_MY_ORGANIZATIONS);

  // Fetch projects
  const { data: projectsData, loading: projectsLoading, refetch: refetchProjects } = useQuery(GET_MY_PROJECTS);

  const organizations = orgsData?.myOrganizations || [];
  const projects = projectsData?.myProjects || [];

  // Auto-select first organization if none selected
  useEffect(() => {
    if (!currentOrganization && organizations.length > 0) {
      // Try to load from localStorage first
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      const savedOrg = organizations.find((org: Organization) => org.id === savedOrgId);
      setCurrentOrganization(savedOrg || organizations[0]);
    }
  }, [organizations, currentOrganization]);

  // Auto-select first project if none selected and we have an org
  useEffect(() => {
    if (currentOrganization && !currentProject && projects.length > 0) {
      const orgProjects = projects.filter((p: Project) => p.organizationId === currentOrganization.id);
      if (orgProjects.length > 0) {
        // Try to load from localStorage first
        const savedProjectId = localStorage.getItem('currentProjectId');
        const savedProject = orgProjects.find((p: Project) => p.id === savedProjectId);
        setCurrentProject(savedProject || orgProjects[0]);
      }
    }
  }, [projects, currentOrganization, currentProject]);

  // Save to localStorage when changed
  useEffect(() => {
    if (currentOrganization) {
      localStorage.setItem('currentOrganizationId', currentOrganization.id);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentProject) {
      localStorage.setItem('currentProjectId', currentProject.id);
    }
  }, [currentProject]);

  const value: OrganizationContextType = {
    currentOrganization,
    currentProject,
    organizations,
    projects: currentOrganization
      ? projects.filter((p: Project) => p.organizationId === currentOrganization.id)
      : [],
    setCurrentOrganization: (org) => {
      setCurrentOrganization(org);
      // Clear current project when switching orgs
      setCurrentProject(null);
    },
    setCurrentProject,
    loading: orgsLoading || projectsLoading,
    refetchOrganizations: refetchOrgs,
    refetchProjects,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
